/** How a catalogue item's quantity is determined. */
export enum BillingItemTypeEnum {
  FIXED_QUANTITY = 'FIXED_QUANTITY',
  ATTENDANCE_BASED = 'ATTENDANCE_BASED'
}

/**
 * The `*_LABELS` maps below hold translation keys, not text: render them through `t()`.
 * They stay maps rather than template strings so the compiler still insists that every
 * enum member has a label - add a member, and the map fails to compile until it has one.
 */
export const BILLING_ITEM_TYPE_LABELS: Record<BillingItemTypeEnum, string> = {
  [BillingItemTypeEnum.FIXED_QUANTITY]: 'enums.billingItemType.FIXED_QUANTITY',
  [BillingItemTypeEnum.ATTENDANCE_BASED]: 'enums.billingItemType.ATTENDANCE_BASED'
};

export enum InvoiceLineTypeEnum {
  FIXED_QUANTITY = 'FIXED_QUANTITY',
  ATTENDANCE_BASED = 'ATTENDANCE_BASED',
  MANUAL = 'MANUAL',
  /** A negative line lowering exactly one other line of the same invoice. */
  DISCOUNT = 'DISCOUNT'
}

/** A discount is a percentage of its line or a fixed amount off it. */
export enum DiscountModeEnum {
  PERCENT = 'PERCENT',
  AMOUNT = 'AMOUNT'
}

export enum InvoiceStatusEnum {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  CANCELLED = 'CANCELLED'
}

export enum InvoicePaymentStatusEnum {
  UNPAID = 'UNPAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID'
}

export enum InvoiceScriptEnum {
  LATIN = 'LATIN',
  CYRILLIC = 'CYRILLIC'
}

/**
 * Each maps to a plain column on the backend. There is deliberately no key for the
 * Remaining column: it is computed, and the invoice query cannot order by an
 * expression without breaking the subquery TypeORM builds for paging.
 */
export enum InvoicesSortKeyEnum {
  NUMBER = 'number',
  CHILD = 'child',
  PERIOD = 'period',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  TOTAL = 'total',
  STATUS = 'status',
  PAYMENT_STATUS = 'paymentStatus',
  /** What is still owed; computed on the server. */
  REMAINING = 'remaining'
}

export const INVOICE_LINE_TYPE_LABELS: Record<InvoiceLineTypeEnum, string> = {
  [InvoiceLineTypeEnum.FIXED_QUANTITY]: 'enums.invoiceLineType.FIXED_QUANTITY',
  [InvoiceLineTypeEnum.ATTENDANCE_BASED]: 'enums.invoiceLineType.ATTENDANCE_BASED',
  [InvoiceLineTypeEnum.MANUAL]: 'enums.invoiceLineType.MANUAL',
  [InvoiceLineTypeEnum.DISCOUNT]: 'enums.invoiceLineType.DISCOUNT'
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatusEnum, string> = {
  [InvoiceStatusEnum.DRAFT]: 'enums.invoiceStatus.DRAFT',
  [InvoiceStatusEnum.ISSUED]: 'enums.invoiceStatus.ISSUED',
  [InvoiceStatusEnum.CANCELLED]: 'enums.invoiceStatus.CANCELLED'
};

/** Only meaningful on an issued invoice; a draft is always reported as unpaid. */
export const INVOICE_PAYMENT_STATUS_LABELS: Record<InvoicePaymentStatusEnum, string> = {
  [InvoicePaymentStatusEnum.UNPAID]: 'enums.invoicePaymentStatus.UNPAID',
  [InvoicePaymentStatusEnum.PARTIALLY_PAID]: 'enums.invoicePaymentStatus.PARTIALLY_PAID',
  [InvoicePaymentStatusEnum.PAID]: 'enums.invoicePaymentStatus.PAID'
};

/** Which rule produced a preview's lines. */
export enum BillingSourceEnum {
  CHILD_PROFILE = 'CHILD_PROFILE',
  GROUP_SCHEME = 'GROUP_SCHEME',
  NONE = 'NONE'
}

/** What a child still lacks before it can be invoiced; the values of `ChildContractDto.missing`. */
export enum ContractMissingEnum {
  MEMBERSHIP = 'membership',
  CONTRACT_NUMBER = 'contractNumber',
  PAYER = 'payer',
  PRICE = 'price'
}

/**
 * The Contracts list filter. Every value but `ready` means "lacks this", so a child
 * lacking two things is under both chips.
 */
export enum ContractStatusFilterEnum {
  READY = 'ready',
  NUMBER = 'number',
  PAYER = 'payer',
  PRICE = 'price',
  GROUP = 'group'
}

/** The one state the list shows an invoice in. Payment wins over delivery. */
export enum InvoiceStateEnum {
  DRAFT = 'DRAFT',
  ISSUED_NOT_SENT = 'ISSUED_NOT_SENT',
  SENT = 'SENT',
  SEND_FAILED = 'SEND_FAILED',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED'
}

/** The list's state filter; `unpaid` is every issued invoice with nothing paid, sent or not. */
export enum InvoiceStateFilterEnum {
  DRAFT = 'draft',
  ISSUED_NOT_SENT = 'issuedNotSent',
  SENT = 'sent',
  SEND_FAILED = 'sendFailed',
  UNPAID = 'unpaid',
  PARTIALLY_PAID = 'partiallyPaid',
  PAID = 'paid',
  CANCELLED = 'cancelled'
}

export enum GenerationPreviewOutcomeEnum {
  CREATE = 'CREATE',
  HAS_INVOICE = 'HAS_INVOICE',
  NOT_READY = 'NOT_READY'
}

export enum IssueOutcomeEnum {
  ISSUED = 'ISSUED',
  SKIPPED = 'SKIPPED',
  FAILED = 'FAILED'
}

/** Lifecycle of a mass generation run. */
export enum InvoiceBatchStatusEnum {
  DRAFTS_READY = 'DRAFTS_READY',
  ISSUED = 'ISSUED'
}

/** What a run did about one child. */
export enum InvoiceBatchOutcomeEnum {
  CREATED = 'CREATED',
  ISSUED = 'ISSUED',
  SKIPPED = 'SKIPPED',
  FAILED = 'FAILED'
}

export const BATCH_OUTCOME_LABELS: Record<InvoiceBatchOutcomeEnum, string> = {
  [InvoiceBatchOutcomeEnum.CREATED]: 'enums.batchOutcome.CREATED',
  [InvoiceBatchOutcomeEnum.ISSUED]: 'enums.batchOutcome.ISSUED',
  [InvoiceBatchOutcomeEnum.SKIPPED]: 'enums.batchOutcome.SKIPPED',
  [InvoiceBatchOutcomeEnum.FAILED]: 'enums.batchOutcome.FAILED'
};

export enum PaymentMethodEnum {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASH = 'CASH',
  OTHER = 'OTHER'
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodEnum, string> = {
  [PaymentMethodEnum.BANK_TRANSFER]: 'enums.paymentMethod.BANK_TRANSFER',
  [PaymentMethodEnum.CASH]: 'enums.paymentMethod.CASH',
  [PaymentMethodEnum.OTHER]: 'enums.paymentMethod.OTHER'
};

/** Whether the invoice's PDF exists. Separate from delivery to the payer. */
export enum InvoicePdfStatusEnum {
  PENDING = 'PENDING',
  QUEUED = 'QUEUED',
  READY = 'READY',
  FAILED = 'FAILED'
}

/** Whether the payer has been sent the invoice. Separate from whether a PDF exists. */
export enum InvoiceDeliveryStatusEnum {
  NOT_SENT = 'NOT_SENT',
  QUEUED = 'QUEUED',
  SENT = 'SENT',
  FAILED = 'FAILED'
}

export const DELIVERY_STATUS_LABELS: Record<InvoiceDeliveryStatusEnum, string> = {
  [InvoiceDeliveryStatusEnum.NOT_SENT]: 'enums.deliveryStatus.NOT_SENT',
  [InvoiceDeliveryStatusEnum.QUEUED]: 'enums.deliveryStatus.QUEUED',
  [InvoiceDeliveryStatusEnum.SENT]: 'enums.deliveryStatus.SENT',
  [InvoiceDeliveryStatusEnum.FAILED]: 'enums.deliveryStatus.FAILED'
};

/**
 * Where one address an invoice goes to stands. WITHDRAWN: queued, then left out of a later
 * send of the same invoice before it went.
 */
export enum InvoiceRecipientStatusEnum {
  QUEUED = 'QUEUED',
  SENT = 'SENT',
  FAILED = 'FAILED',
  WITHDRAWN = 'WITHDRAWN'
}

export const RECIPIENT_STATUS_LABELS: Record<InvoiceRecipientStatusEnum, string> = {
  [InvoiceRecipientStatusEnum.QUEUED]: 'enums.recipientStatus.QUEUED',
  [InvoiceRecipientStatusEnum.SENT]: 'enums.recipientStatus.SENT',
  [InvoiceRecipientStatusEnum.FAILED]: 'enums.recipientStatus.FAILED',
  [InvoiceRecipientStatusEnum.WITHDRAWN]: 'enums.recipientStatus.WITHDRAWN'
};

/** Which level of the kindergarten a finance summary describes. */
export enum FinanceScopeEnum {
  KINDERGARTEN = 'KINDERGARTEN',
  LOCATION = 'LOCATION',
  GROUP = 'GROUP'
}

/** What one breakdown row is. Sent by the server so the client never re-derives it. */
export enum FinanceGrainEnum {
  LOCATION = 'LOCATION',
  GROUP = 'GROUP',
  CHILD = 'CHILD'
}
